import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import validator from 'validator';

import { USER_ROLE } from '@/config/constants';
import { useSignUpMutation } from '@/apis/extended/authApi';
import { navigateByRole } from './authnavigation';

type SignUpModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const SignUpModal = ({ isOpen, onClose }: SignUpModalProps) => {
  const theme = useTheme();
  const [signUp] = useSignUpMutation();
  const [isVerifyRequired, setIsVerifyRequired] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSignUp = () => {
    setError('');
    if (!validator.isEmail(email)) {
      setError('Please input valid email address.');
      return;
    }
    if (!validator.isLength(password, { min: 8, max: 20 })) {
      setError('Password must be at least 8 characters.');
      return;
    }
    signUp({ email, password })
      .unwrap()
      .then((res) => {
        if (res.verify) {
          setIsVerifyRequired(true);
          return;
        }
        localStorage.setItem('role', USER_ROLE.BUYER);
        navigateByRole(USER_ROLE.BUYER);
      })
      .catch((err: { data?: { message?: string }; status?: number }) => {
        const msg =
          typeof err?.data === 'string'
            ? err.data
            : err?.data?.message ?? 'Sign up failed. Please try again.';
        setError(msg);
      });
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogContent
        sx={{ py: '60px', px: '32px', width: '450px', maxWidth: '100%' }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h2">Welcome to</Typography>
          <Typography
            variant="h2"
            sx={{ color: theme.palette.mainColors.blue }}
          >
            Unbeatable loans
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.greyShades.grey1 }}
          >
            Get started by creating an account. It&apos;s free.
          </Typography>
        </Box>
        <Box sx={{ mt: 5 }}>
          <TextField
            label="Email"
            fullWidth
            size="small"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            helperText={error}
            error={!!error}
          />
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Password"
              fullWidth
              size="small"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Box>

          {isVerifyRequired ? (
            <Typography
              variant="body1"
              sx={{
                mt: 3,
                textAlign: 'center',
                color: theme.palette.error.main,
              }}
            >
              Your account is secured.
              <br />
              Please check your email to complete Sign Up.
            </Typography>
          ) : (
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              onClick={handleSignUp}
            >
              Sign Up
            </Button>
          )}
        </Box>

        {!isVerifyRequired && (
          <Box
            sx={{ mt: 1, textAlign: 'center', maxWidth: '300px', mx: 'auto' }}
          >
            <Typography variant="caption">
              By continuing, you agree to our{' '}
              <Link to="/toc">Terms of services</Link> and{' '}
              <Link to="/privacy-policy">Privacy Policy</Link>.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
